import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './PublicationCaseStudy.module.css';

export default function RPETPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const img = (name) => `/assets/publication/rpet-additive-manufacturing/${name}`;

  return (
    <div className={styles.articleContainer}>
      <button className={styles.backButton} onClick={() => navigate('/research')}>
        ← Back to Research
      </button>

      <header className={styles.header}>
        <div className={styles.badgeRow}>
          <div
            className={styles.badge}
            style={{
              background: 'rgba(16, 185, 129, 0.12)',
              color: '#047857',
              borderColor: 'rgba(16, 185, 129, 0.25)'
            }}
          >
            🥈 Second Place Tie — AIAA Student Conference VII, 2025 (Paper Team Category)
          </div>
          <div
            className={styles.badge}
            style={{
              background: 'rgba(99, 102, 241, 0.12)',
              color: '#4338ca',
              borderColor: 'rgba(99, 102, 241, 0.25)'
            }}
          >
            📄 Under Review — Rapid Prototyping Journal
          </div>
        </div>
        <h1 className={styles.title}>
          Sustainable Additive Manufacturing using rPET: Extrusion Design, Mechanical Testing, and Data-Driven Prediction
        </h1>
        <div className={styles.authors}>
          <span className={styles.highlightAuthor}>Jolok Banarjee</span>, Holyjith Paul Himel, Toymor Wafi Opul, Tuhin Alahi Anny, Shahrukh Khan
        </div>
        <div className={styles.affiliations}>
          Department of Aerospace Engineering, Aviation and Aerospace University, Bangladesh
        </div>

        <div className={styles.headerActions}>
          <button
            onClick={() => navigate('/projects/rpet-additive-manufacturing')}
            className={styles.btnPrimary}
          >
            🔗 View Project Details →
          </button>
        </div>
      </header>

      <div className={styles.abstractBox}>
        <h3 className={styles.abstractTitle}>Abstract</h3>
        <p className={styles.abstractText}>
          As global plastic waste grows, practical, small-scale recycling systems are urgently needed. This study presents an affordable, automated extrusion system converting post-consumer polyethylene terephthalate (PET) bottles into functional 1.75 mm filament for fused filament fabrication (FFF). The mechanical strength of the custom-extruded recycled PET (rPET) was evaluated via ASTM D638 tensile testing and directly compared with commercial PLA, ABS, and PETG. The optimized rPET achieved an ultimate tensile strength of 45.31 MPa, comparable to virgin PLA (46.44 MPa). Furthermore, it demonstrated superior ductility (8.7% elongation) and toughness (2.23 MJ/m³), outperforming PLA and ABS in impact resistance. An XGBoost machine learning model was also developed to predict the non-linear stress-strain curves of the polymers. Following rigorous 15-Fold Group Cross-Validation, the model achieved exceptional predictive accuracy (RMSE = 0.901 MPa), with SHAP analysis confirming its reliance on physical metrics over mathematical noise. Yielding an average material recovery efficiency of 53.8%, this study proves the structural, computational, and environmental viability of locally recycled rPET as a high-performance, closed-loop alternative to commercial thermoplastics in additive manufacturing.
        </p>
        <div className={styles.keywords}>
          <strong>Keywords:</strong> Additive Manufacturing, Recycled PET (rPET), Filament Extrusion, Mechanical Characterization, Circular Economy, Machine Learning (XGBoost), SHAP Explainability
        </div>
      </div>

      <section>
        <h2 className={styles.sectionTitle}>Introduction</h2>
        <p className={styles.paragraph}>
          Global plastic production has increased exponentially over the past seven decades, rising from 2 Mt in 1950 to 400 Mt in 2022, with projections to reach 800 Mt by 2050. Alarmingly, less than 10% of plastics produced are made from recycled content. Because conventional plastics can take up to 400 years to biodegrade, their gradual breakdown into microplastic particles poses severe risks to human and ecological health.
        </p>
        <p className={styles.paragraph}>
          Additive manufacturing (AM), particularly Fused Filament Fabrication (FFF), offers a promising pathway to reduce plastic waste by enhancing the circular use of recyclable polymers such as polyethylene terephthalate (PET). This research bridges the gap between environmental sustainability and high-performance engineering materials by designing a complete bottle-to-filament pipeline and validating it through rigorous mechanical benchmarking and machine learning.
        </p>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Methodology</h2>
        <p className={styles.paragraph}>
          The custom decentralized extrusion system converts post-consumer PET bottles into rPET filament through three stages: mechanical strip cutting, thermal filament extrusion, and automated filament winding. The machine integrates a PID-controlled heating block (REX-C100 thermostat + SSR-25DD + 40W MK8 heater), a K-type thermocouple, and an Arduino-driven NEMA 17 winding mechanism. Extrusion temperature was fixed at 220°C to minimize internal void formation and maximize mechanical strength.
        </p>
        <div className={styles.galleryGrid}>
          <figure className={styles.galleryItem}>
            <img src={img('cover.jpg')} alt="rPET Extrusion Machine" />
            <figcaption>Figure 1: The developed extrusion system showing the strip holder, heating block, filament winder, and closed-loop temperature control system.</figcaption>
          </figure>
          <figure className={styles.galleryItem}>
            <img src={img('buttol.jpg')} alt="rPET Strips" />
            <figcaption>Figure 2: Post-consumer PET bottles converted into continuous strips ready for thermal extrusion.</figcaption>
          </figure>
          <figure className={styles.galleryItem}>
            <img src={img('rPET_filament.jpg')} alt="rPET Filament" />
            <figcaption>Figure 3: Finished 1.75 mm rPET filament spools produced from recycled PET bottles.</figcaption>
          </figure>
          <figure className={styles.galleryItem}>
            <img src={img('using_rPET_filament_3D_print.jpg')} alt="3D Printing with rPET" />
            <figcaption>Figure 4: rPET filament being used in a Bambu Lab X1 Carbon 3D printer to fabricate ASTM D638 test specimens.</figcaption>
          </figure>
        </div>

        <p className={styles.paragraph}>
          Test specimens were designed to ASTM D638 Type IV specifications and printed with 100% rectilinear infill at 30 mm/s. For the custom rPET filament, an optimal extrusion temperature of 255°C was established to minimize inter-layer gaps and maximize layer adhesion. A constant crosshead displacement rate of 5 mm/min was maintained for quasi-static loading until fracture.
        </p>
        <div className={styles.galleryGrid}>
          <figure className={styles.galleryItem}>
            <img src={img('Specimen dimension.jpg')} alt="ASTM D638 Dimensions" />
            <figcaption>Figure 5: SolidWorks CAD drawing of the ASTM D638 Type IV tensile test specimen geometry (all dimensions in mm).</figcaption>
          </figure>
          <figure className={styles.galleryItem}>
            <img src={img('specimen iso view.PNG')} alt="Specimen ISO View" />
            <figcaption>Figure 6: Isometric view of the 3D-printed tensile test specimen with key dimensions.</figcaption>
          </figure>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Experimental Results</h2>
        <p className={styles.paragraph}>
          Comparative tensile testing of four 3D-printed thermoplastics was conducted under identical ASTM D638 conditions. The custom-extruded rPET proved to be a high-performance, closed-loop alternative, demonstrating superior ductility and impact resistance compared to standard PLA and ABS.
        </p>

        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>45.31 MPa</div>
            <div className={styles.metricLabel}>Ultimate Tensile Strength</div>
            <p className={styles.metricSubtext}>
              Retains 98.5% of virgin PLA strength (46.44 MPa).
            </p>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>8.7%</div>
            <div className={styles.metricLabel}>Elongation at Break</div>
            <p className={styles.metricSubtext}>
              Nearly 2× PLA ductility; superior impact resistance.
            </p>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>0.901</div>
            <div className={styles.metricLabel}>XGBoost RMSE</div>
            <p className={styles.metricSubtext}>
              Predictive accuracy for continuous stress-strain curves.
            </p>
          </div>
        </div>

        <p className={styles.paragraph}>
          The rPET specimens displayed distinct failure mechanisms compared to commercial filaments. Whereas PLA and ABS exhibited abrupt brittle fracture, the rPET specimens visibly stretched, thinned, and stress-whitened before rupture — a hallmark of ductile failure. The modulus of toughness reached 2.23 MJ/m³, nearly double that of virgin PLA (1.19 MJ/m³).
        </p>

        <div className={styles.galleryGrid}>
          <figure className={styles.galleryItem}>
            <img src={img('PLA ABS PETG rPET Specimens.jpg')} alt="Comparative Specimens" />
            <figcaption>Figure 7: Comparative 3D-printed specimens (PLA, ABS, PETG, and rPET) before mechanical testing.</figcaption>
          </figure>
          <figure className={styles.galleryItem}>
            <img src={img('printed specimens after tensile test.jpg')} alt="After Tensile Test" />
            <figcaption>Figure 8: Post-fracture specimens showing brittle failure in PLA/ABS versus ductile necking in rPET.</figcaption>
          </figure>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Parametric Investigation</h2>
        <p className={styles.paragraph}>
          Two rPET variants derived from locally available beverage bottles were evaluated: rPET-1 (0.25 mm bottle thickness, 8.5 mm strip width) and rPET-2 (0.475 mm bottle thickness, 7.0 mm strip width). Filament derived from thinner bottles achieved higher UTS (45.31 MPa) and ductility (8.65%) due to superior melting homogeneity, while thicker strips offered improved manufacturing predictability with lower variance (±2.00 MPa vs ±5.24 MPa).
        </p>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Data-Driven Prediction (XGBoost)</h2>
        <p className={styles.paragraph}>
          An XGBoost regressor was trained on 15,738 discrete data points across 15 physical specimens to predict continuous engineering stress from real-time load-displacement data. Following systematic feature selection to prevent data leakage — removing geometric dimensions, multicollinear pairs, and categorical identifiers — the model was optimized via Bayesian Optimization (Optuna), Grid Search, and Randomized Search under 15-Fold Group Cross-Validation.
        </p>
        <p className={styles.paragraph}>
          The optimized model achieved an RMSE of 0.901 MPa and R² of 0.993. SHAP interpretability analysis confirmed that the algorithm relied on genuine physical metrics: Force (kgf) as the primary driver, Yield Stress as a material anchor, and Distortion (mm) for strain context — validating that the model learned solid mechanics rather than mathematical noise.
        </p>
        <div className={styles.galleryGrid}>
          <figure className={styles.galleryItem}>
            <img src={img('Comparison_Plot_rPET_1_0.25mm.png')} alt="rPET-1 Predictions" />
            <figcaption>Figure 9: Individual specimen predictions for rPET-1 (0.25 mm) showing strong agreement with experimental curves.</figcaption>
          </figure>
          <figure className={styles.galleryItem}>
            <img src={img('Comparison_Plot_rPET_2_0.475mm.png')} alt="rPET-2 Predictions" />
            <figcaption>Figure 10: Individual specimen predictions for rPET-2 (0.475 mm) validating model generalization across thickness variants.</figcaption>
          </figure>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Conclusion</h2>
        <p className={styles.paragraph}>
          This study successfully demonstrated a complete, accessible system for converting post-consumer PET bottles into functional 3D printing filament. The custom-extruded rPET achieved an ultimate tensile strength of 45.31 MPa — nearly identical to virgin PLA — while offering significantly superior ductility (8.7% elongation) and toughness (2.23 MJ/m³). The integrated XGBoost model (RMSE 0.901 MPa) and SHAP interpretability analysis establish a reliable virtual testing framework for sustainable additive manufacturing. With a 53.8% material recovery rate and 50–75% cost reduction over commercial filament, this work provides a scalable blueprint for decentralized circular manufacturing in aerospace and beyond.
        </p>
      </section>

      <div
        className={styles.transparencyNote}
        style={{
          background: 'rgba(99, 102, 241, 0.06)',
          border: '1px dashed rgba(99, 102, 241, 0.35)'
        }}
      >
        <strong style={{ color: '#4338ca' }}>🔍 Transparency Note:</strong>{' '}
        This manuscript is currently under peer review at <em>Rapid Prototyping Journal</em>. All performance metrics, figures, and methodology descriptions reflect the submitted version. Final published values may differ following journal revision. An earlier draft of this research received Second Place (Tie) in the Paper Team Category at the 2025 AIAA Region VII Student Conference.
      </div>

    </div>
  );
}